import { PublicClientApplication } from '@azure/msal-browser';
import { MS_CLIENT_ID, MS_AUTHORITY, MS_SCOPES, MS_REDIRECT_URI } from './config';

// MSAL instance (singleton)
let msalInstance = null;

const msalConfig = {
  auth: {
    clientId: MS_CLIENT_ID,
    authority: MS_AUTHORITY,
    redirectUri: MS_REDIRECT_URI,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true,
  },
};

export function getMsalInstance() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

export async function initMsAuth() {
  const msal = getMsalInstance();
  await msal.initialize();

  // Handle redirect response (if returning from Microsoft login)
  try {
    const response = await msal.handleRedirectPromise();
    if (response) {
      return response.account;
    }
  } catch (e) {
    console.warn('MSAL redirect error:', e);
  }

  // Check for existing session
  const accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    return accounts[0];
  }
  return null;
}

export async function msSignIn() {
  const msal = getMsalInstance();
  // Use redirect flow — more reliable than popup (no cross-origin issues)
  // This will navigate away from the page, then come back after auth
  // The response is handled in initMsAuth() via handleRedirectPromise()
  await msal.loginRedirect({
    scopes: MS_SCOPES,
    prompt: 'select_account',
  });
  // This line won't be reached — page navigates away
  return null;
}

export async function msSignOut() {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    // Clear session storage and local account
    sessionStorage.removeItem('harmonee_user');
    await msal.logoutRedirect({
      account: accounts[0],
      postLogoutRedirectUri: MS_REDIRECT_URI,
    });
  }
}

export async function getMsToken() {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msal.acquireTokenSilent({
      scopes: MS_SCOPES,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (e) {
    // Silent token failed — use redirect to re-authenticate
    try {
      await msal.acquireTokenRedirect({
        scopes: MS_SCOPES,
        account: accounts[0],
      });
    } catch (err) {
      console.error('Failed to acquire MS token:', err);
    }
    return null;
  }
}

export function getMsAccount() {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}
